/**
 * The daily detail drawer — one (employee, date), and where its numbers came from.
 *
 * `GET /api/v1/attendance/{employeeId}/{date}`, which is a **different endpoint from
 * the grid** and is read again rather than looked up in the page already in memory.
 * Two reasons, and the second is the point of the drawer:
 *
 *  1. The row may have been recomputed since the month was fetched, and this is the
 *     surface where somebody decides something about a person.
 *  2. It is the read that carries `shift` — the resolution and the provenance block.
 *
 * ── Data source and completeness are not a footnote ─────────────────────────
 *
 * `web/shapes/attendance.md` §3 and the plan both make this the drawer's job, and
 * API_CONTRACT §8.3 says why: *"Con số attendance không kèm completeness là con số
 * nói dối"*. Concretely, the two blocks below are the mechanism by which a manager
 * can tell **"this person was absent"** from **"we could not see this person"**:
 *
 *   `session_row_present: false`   there is no record at all; every shift-relative
 *                                  figure above is withheld, not zero.
 *   `completeness.status`          which of the five things is true of what arrived.
 *   `contributing_device_ids`      which machines produced it, and how many.
 *   `stale`                        the row was built by an algorithm this image does
 *                                  not run, so the one accusatory verdict is held.
 *   `last_event_received_at`       whether more is still landing.
 *
 * They render **above the fold of the drawer**, immediately under the figures they
 * qualify, rather than at the bottom under a disclosure — a completeness a reader
 * has to open is a completeness a reader does not read.
 *
 * ── There is no control here that files a correction for somebody else ──────
 *
 * Filing is refused server-side for any subject but yourself, whatever the caller
 * holds (`assertSubjectIsSelf` plus step 5 of the 02 §11 chain), because an
 * explanation is that person's own account of their day. So the drawer states that
 * and offers no button; the employee files from `/my-activity`.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay, formatInstant } from '../../i18n/datetime';
import { attendanceDetailPath } from '../../api/paths';
import type { AttendanceGridDay, AttendanceMetricName } from '../../api/types';
import { ATTENDANCE_METRIC_NAMES } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import {
  COMPLETENESS_KEY,
  readDuration,
  readMetric,
  renderReading,
  STATUS_PRESENTATION,
} from '../../attendance/status';
import { AgentOffline } from '../../states/AgentOffline';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { DataDelayed } from '../../states/DataDelayed';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { StatusBadge } from './StatusBadge';

const METRIC_LABEL: Readonly<Record<AttendanceMetricName, `k3.metric.${AttendanceMetricName}`>> = {
  late_minutes: 'k3.metric.late_minutes',
  early_leave_minutes: 'k3.metric.early_leave_minutes',
  missing_minutes: 'k3.metric.missing_minutes',
  overtime_minutes: 'k3.metric.overtime_minutes',
};

export interface DayDrawerProps {
  readonly employeeId: string;
  readonly date: string;
  readonly timezone: string;
  readonly onClose: () => void;
}

export function DayDrawer({ employeeId, date, timezone, onClose }: DayDrawerProps): ReactNode {
  const { t, locale } = useI18n();
  const { status, data, error, reload } = useApiQuery<AttendanceGridDay>(
    attendanceDetailPath(employeeId, date),
  );
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Focus lands on the drawer's own heading when it opens. `web/shapes/attendance.md`
  // §8: without this a keyboard user is left at the cell they pressed while the new
  // content is somewhere below, and a screen reader announces nothing at all.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // `Esc` closes. The caller returns focus to the control that opened the drawer,
  // which is the half that matters: focus left at the top of the document means
  // re-walking the whole month to get back to where you were.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  const dateLabel = formatDay(data === null ? date : data.date, locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const title =
    data === null
      ? t('k3.drawer.title', { employee: employeeId, date: dateLabel })
      : t('k3.drawer.title', { employee: data.employee_name, date: dateLabel });

  return (
    // `role="dialog"` and deliberately **no `aria-modal`**. This is a panel in the
    // flow, not an overlay: nothing outside it is inert, and there is no focus trap
    // (`app.css` says why — a trap implemented incorrectly loses a keyboard user in
    // the page behind it). `aria-modal="true"` would tell a screen reader the rest of
    // the document is unavailable, which is a guarantee this component does not keep,
    // and the reader would then be unable to reach the roster underneath.
    <aside
      className="wp-drawer"
      role="dialog"
      aria-labelledby="k3-drawer-title"
      data-testid="k3-drawer"
    >
      <div className="wp-screen-head">
        <h2 className="wp-section__title" id="k3-drawer-title" tabIndex={-1} ref={headingRef}>
          {title}
        </h2>
        <button type="button" className="wp-button" onClick={onClose} data-testid="k3-drawer-close">
          {t('k3.drawer.close')}
        </button>
      </div>

      {status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('k3.drawer.loading')}
          testId="k3-drawer-skeleton"
        />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('k3.drawer.subject')}
          testId="k3-drawer-error"
        />
      ) : null}

      {status === 'ok' && data !== null ? (
        <div className="wp-stack">
          <p>
            <StatusBadge status={data.attendance_status} testId="k3-drawer-badge" />
          </p>
          <p className="wp-small wp-muted" data-testid="k3-drawer-status-help">
            {t(STATUS_PRESENTATION[data.attendance_status].helpKey)}
          </p>

          <p data-testid="k3-drawer-window">
            {t('k3.drawer.window', {
              first:
                data.first_activity_at === null
                  ? t('attendance.noFirst')
                  : formatInstant(data.first_activity_at, locale, timezone),
              last:
                data.last_activity_at === null
                  ? t('attendance.noLast')
                  : formatInstant(data.last_activity_at, locale, timezone),
            })}
          </p>

          <ShiftBlock day={data} />

          {/* `AgentOffline` **replaces** the shift-relative figures when nothing was
              observed and a device was expected. It takes no numeric prop at all, so
              this branch cannot render "0 ph" even by accident — which is the
              guarantee that component exists for (PRODUCT.md §4, seventh row). */}
          {data.completeness.status === 'missing' ? (
            <AgentOffline
              liveness="offline"
              lastHeartbeatAt={data.shift.source.last_event_received_at}
              timezone={timezone}
              deviceLabel={
                data.shift.source.contributing_device_ids[0] ?? t('attendance.agentDevice')
              }
              testId="k3-drawer-agent-offline"
            />
          ) : (
            <section aria-labelledby="k3-drawer-metrics">
              <h3 className="wp-section__title" id="k3-drawer-metrics">
                {t('k3.drawer.metrics')}
              </h3>
              <dl className="wp-figures" data-testid="k3-drawer-metrics">
                {ATTENDANCE_METRIC_NAMES.map((name) => (
                  <div key={name} className="wp-figures__item">
                    <dt>{t(METRIC_LABEL[name])}</dt>
                    <dd data-testid={`k3-drawer-${name}`}>
                      {renderReading(readMetric(data[name], data.withheld, name), durations, t)}
                    </dd>
                  </div>
                ))}
                <div className="wp-figures__item">
                  <dt>{t('attendance.online')}</dt>
                  <dd data-testid="k3-drawer-online">
                    {renderReading(readDuration(data.online_minutes, data.reason), durations, t)}
                  </dd>
                </div>
                <div className="wp-figures__item">
                  <dt title={t('attendance.idle.help')}>{t('attendance.idle')}</dt>
                  <dd data-testid="k3-drawer-idle">
                    {renderReading(readDuration(data.idle_minutes, data.reason), durations, t)}
                  </dd>
                </div>
                <div className="wp-figures__item">
                  <dt>{t('attendance.approvedOffline')}</dt>
                  <dd data-testid="k3-drawer-approved-offline">
                    {renderReading(
                      readDuration(data.approved_offline_minutes, data.reason),
                      durations,
                      t,
                    )}
                  </dd>
                </div>
              </dl>
            </section>
          )}

          <SourceBlock day={data} timezone={timezone} />
          <CompletenessBlock day={data} />

          {data.completeness.status === 'partial' ? (
            <PartialData
              excludes={t(COMPLETENESS_KEY.partial)}
              score={data.completeness.score}
              deviceCount={data.completeness.device_count}
              testId="k3-drawer-partial"
            />
          ) : null}

          {data.completeness.unsynced_data ? (
            <DataDelayed
              source="processing"
              since={data.shift.source.last_event_received_at}
              timezone={timezone}
              detail={t('attendance.unsynced')}
              testId="k3-drawer-unsynced"
            />
          ) : null}

          {(data.approved_offline_minutes ?? 0) > 0 ? (
            <p data-testid="k3-drawer-credited">
              {t('k3.drawer.credited', {
                minutes: renderReading(
                  readDuration(data.approved_offline_minutes, data.reason),
                  durations,
                  t,
                ),
              })}
            </p>
          ) : null}

          <p className="wp-small wp-muted" data-testid="k3-drawer-correction-note">
            {t('k3.drawer.correctionSelfOnly')}
          </p>
        </div>
      ) : null}
    </aside>
  );
}

/** Which shift answered, and through which rung of the precedence order. */
function ShiftBlock({ day }: { readonly day: AttendanceGridDay }): ReactNode {
  const { t } = useI18n();
  const shift = day.shift;
  const via = shift.via;
  const line =
    shift.outcome === 'assigned' && via !== null
      ? t('k3.drawer.shift.assigned', {
          code: shift.shift_code ?? '—',
          scope: via.scope_type,
          key: via.scope_key,
          priority: via.priority,
        })
      : shift.outcome === 'assigned' || shift.outcome === 'system_default'
        ? t('k3.drawer.shift.system_default', { code: shift.shift_code ?? '—' })
        : shift.outcome === 'off_duty'
          ? t('k3.drawer.shift.off_duty')
          : t('k3.drawer.shift.unconfigured');

  return (
    <section aria-labelledby="k3-drawer-shift">
      <h3 className="wp-section__title" id="k3-drawer-shift">
        {t('k3.drawer.shift')}
      </h3>
      <p data-testid="k3-drawer-shift-line">{line}</p>
      {shift.starts_at === undefined || shift.ends_at === undefined ? null : (
        <p className="wp-small wp-muted" data-testid="k3-drawer-shift-window">
          {t('k3.drawer.shift.window', {
            start: shift.starts_at,
            end: shift.ends_at,
            timezone: shift.timezone ?? '—',
            // Elapsed **instants**, so a DST day reads 420 or 540 rather than a
            // silent 480. Printed in minutes because that is the unit the server
            // computed it in and a re-derivation here could disagree with it.
            elapsed: String(shift.elapsed_minutes ?? 0),
          })}
        </p>
      )}
      {shift.grace_late_minutes === undefined ? null : (
        <p className="wp-small wp-muted" data-testid="k3-drawer-shift-grace">
          {t('k3.drawer.shift.grace', {
            late: String(shift.grace_late_minutes),
            early: String(shift.grace_early_leave_minutes ?? 0),
          })}
        </p>
      )}
    </section>
  );
}

/** Where the numbers came from. The half of the drawer the plan asks for by name. */
function SourceBlock({
  day,
  timezone,
}: {
  readonly day: AttendanceGridDay;
  readonly timezone: string;
}): ReactNode {
  const { t, locale } = useI18n();
  const source = day.shift.source;
  return (
    <section aria-labelledby="k3-drawer-source" data-testid="k3-drawer-source">
      <h3 className="wp-section__title" id="k3-drawer-source">
        {t('k3.drawer.source')}
      </h3>
      <p className="wp-small wp-muted">{t('k3.drawer.source.help')}</p>
      <p data-testid="k3-drawer-source-row">
        {source.session_row_present
          ? t('k3.drawer.source.rowPresent')
          : t('k3.drawer.source.rowAbsent')}
      </p>
      <p className="wp-small">
        {t('k3.drawer.source.version', {
          stored: source.session_source_version ?? '—',
          current: source.current_source_version,
        })}
      </p>
      {source.stale ? (
        <p className="wp-small" data-testid="k3-drawer-source-stale">
          {t('k3.drawer.source.stale')}
        </p>
      ) : null}
      <p className="wp-small">
        {source.session_calculated_at === null
          ? t('k3.drawer.source.calculatedNever')
          : t('k3.drawer.source.calculated', {
              when: formatInstant(source.session_calculated_at, locale, timezone),
            })}
      </p>
      <p className="wp-small" data-testid="k3-drawer-source-devices">
        {source.contributing_device_ids.length === 0
          ? t('k3.drawer.source.noDevices')
          : t('k3.drawer.source.devices', { devices: source.contributing_device_ids.join(', ') })}
      </p>
      {source.multi_device_policy === null ? null : (
        <p className="wp-small">
          {t('k3.drawer.source.policy', { policy: source.multi_device_policy })}
        </p>
      )}
      <p className="wp-small">
        {source.last_event_received_at === null
          ? t('k3.drawer.source.lastEventNever')
          : t('k3.drawer.source.lastEvent', {
              when: formatInstant(source.last_event_received_at, locale, timezone),
            })}
      </p>
    </section>
  );
}

/** How much of the day was observable. `status` is authoritative; `score` is presentation. */
function CompletenessBlock({ day }: { readonly day: AttendanceGridDay }): ReactNode {
  const { t } = useI18n();
  const completeness = day.completeness;
  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };
  return (
    <section aria-labelledby="k3-drawer-completeness" data-testid="k3-drawer-completeness">
      <h3 className="wp-section__title" id="k3-drawer-completeness">
        {t('k3.drawer.completeness')}
      </h3>
      <p className="wp-small wp-muted">{t('k3.drawer.completeness.help')}</p>
      <p data-testid="k3-drawer-completeness-status">{t(COMPLETENESS_KEY[completeness.status])}</p>
      <p className="wp-small">
        {t('k3.drawer.completeness.score', {
          percent: String(Math.round(completeness.score * 100)),
        })}
      </p>
      <p className="wp-small">
        {t('k3.drawer.completeness.devices', {
          count: completeness.device_count,
          events: completeness.event_count,
        })}
      </p>
      {completeness.data_gap_seconds > 0 ? (
        <p className="wp-small" data-testid="k3-drawer-gap">
          {t('k3.drawer.completeness.gap', {
            gap: renderReading(
              { kind: 'measured', minutes: completeness.data_gap_seconds / 60 },
              durations,
              t,
            ),
          })}
        </p>
      ) : null}
      {completeness.has_clock_anomaly ? (
        <p className="wp-small" data-testid="k3-drawer-clock-anomaly">
          {t('k3.drawer.completeness.clockAnomaly')}
        </p>
      ) : null}
    </section>
  );
}
